import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserPlus,
  Search,
  Send,
  Copy,
  Loader2,
  Check,
  Upload,
  X,
  Sparkles,
  Heart,
  Scissors,
  Paintbrush,
  Eye,
  ChevronDown,
  ChevronUp,
  Camera,
  Image as ImageIcon,
  User
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category-specific preference field configs
const MAKEUP_FIELDS = {
  skin_type: { label: "Skin Type", type: "pill-select", options: ["Normal", "Dry", "Oily", "Combination", "Sensitive"] },
  skin_concerns: { label: "Skin Concerns", type: "multi-pill", options: ["Acne", "Redness", "Dark spots", "Fine lines", "Texture", "None"] },
  makeup_allergies: { label: "Allergies / Sensitivities", type: "text", placeholder: "e.g., latex, fragrance, certain brands..." },
  special_requests: { label: "Special Requests", type: "textarea", placeholder: "Anything else you'd like your makeup artist to know..." }
};

const HAIR_FIELDS = {
  hair_type: { label: "Hair Type", type: "pill-select", options: ["Straight", "Wavy", "Curly", "Coily", "Locs"] },
  hair_length: { label: "Hair Length", type: "pill-select", options: ["Short", "Medium", "Long", "Extra Long"] },
  hair_concerns: { label: "Hair Concerns", type: "multi-pill", options: ["Dryness", "Frizz", "Breakage", "Thinning", "Color damage", "None"] },
  special_requests: { label: "Special Requests", type: "textarea", placeholder: "Anything else for your stylist..." }
};

const NAILS_FIELDS = {
  nail_shape: { label: "Nail Shape Preference", type: "pill-select", options: ["Round", "Square", "Oval", "Almond", "Coffin", "Stiletto"] },
  nail_length: { label: "Nail Length", type: "pill-select", options: ["Short", "Medium", "Long", "Extra Long"] },
  nail_concerns: { label: "Concerns", type: "multi-pill", options: ["Brittle nails", "Peeling", "Ridges", "Fungus", "None"] },
  special_requests: { label: "Special Requests", type: "textarea", placeholder: "Any specific designs or requests..." }
};

const CULINARY_FIELDS = {
  occasion: { label: "Occasion", type: "pill-select", options: ["Birthday", "Wedding", "Anniversary", "Baby Shower", "Corporate", "Holiday", "Other"] },
  guest_count: { label: "Guest Count", type: "number", placeholder: "Estimated servings" },
  special_requests: { label: "Special Requests", type: "textarea", placeholder: "Flavor preferences, design ideas, specific requests..." }
};

const LASH_FIELDS = {
  lash_style: { label: "Lash Style", type: "pill-select", options: ["Natural", "Wispy", "Dramatic", "Cat Eye", "Doll Eye"] },
  lash_concerns: { label: "Concerns", type: "multi-pill", options: ["Sensitive eyes", "Allergies", "Previous bad reaction", "First time", "None"] },
  special_requests: { label: "Special Requests", type: "textarea", placeholder: "Anything else for your lash tech..." }
};

function getCategoryFields(pilotCategory) {
  const cat = (pilotCategory || "").toLowerCase();
  if (cat === "makeup") return MAKEUP_FIELDS;
  if (cat === "hair") return HAIR_FIELDS;
  if (["nails", "nail"].includes(cat)) return NAILS_FIELDS;
  if (cat === "culinary") return CULINARY_FIELDS;
  if (cat === "lash") return LASH_FIELDS;
  return {};
}

function getCategoryIcon(pilotCategory) {
  const cat = (pilotCategory || "").toLowerCase();
  if (cat === "makeup") return Sparkles;
  if (cat === "hair") return Scissors;
  if (["nails", "nail"].includes(cat)) return Paintbrush;
  if (cat === "lash") return Eye;
  return Heart;
}

export default function InviteClientModal({
  open, onClose, eventId, pilotCategory, brandColor, existingClients = [], hostLooks = [], onSuccess
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSearch, setShowClientSearch] = useState(true);

  // Basic info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [notes, setNotes] = useState("");

  // Category-specific preferences
  const [prefs, setPrefs] = useState({});

  // Inspo
  const [selectedInspo, setSelectedInspo] = useState([]);
  const [showInspoSection, setShowInspoSection] = useState(false);

  // Client photos
  const [clientPhotos, setClientPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotosSection, setShowPhotosSection] = useState(false);

  // Services
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fetchedLooks, setFetchedLooks] = useState([]);

  // Section collapse state
  const [expandedSections, setExpandedSections] = useState({ info: true, service: true, prefs: true, inspo: false, photos: false });

  // Iter 9.47.g - dynamic preference fields fetched live from Airtable.
  // Replaces the hardcoded MAKEUP_FIELDS / HAIR_FIELDS / etc. blocks above.
  const [dynamicFields, setDynamicFields] = useState(null);
  const [universalDefaults, setUniversalDefaults] = useState({});
  const [hostPrefConfig, setHostPrefConfig] = useState(null);

  useEffect(() => {
    if (!pilotCategory) return;
    fetch(`${API}/preferences/category/${(pilotCategory || '').toLowerCase()}/fields`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setDynamicFields(d.fields || []);
        setUniversalDefaults(d.universal_defaults || {});
      })
      .catch(() => setDynamicFields([]));
  }, [pilotCategory]);

  // Pull the host's per-experience config (which preferences to collect,
  // and whether allergies/dietary/accessibility are required).
  useEffect(() => {
    if (!eventId || !serviceId) return;
    const hostId = (services.find(s => s.id === serviceId) || {}).provider_id;
    if (!hostId) return;
    fetch(`${API}/host/${hostId}/experiences/${serviceId}/preference-config`)
      .then(r => r.ok ? r.json() : null)
      .then(setHostPrefConfig)
      .catch(() => {});
  }, [eventId, serviceId, services]);

  const photoInputRef = useRef(null);
  const token = localStorage.getItem("session_token");

  useEffect(() => {
    if (open && eventId) {
      fetchServices();
      fetchHostLooks();
      resetForm();
    }
  }, [open, eventId]);

  const resetForm = () => {
    setSearchQuery("");
    setSelectedClient(null);
    setShowClientSearch(true);
    setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setServiceId(""); setAppointmentDate(""); setNotes("");
    setPrefs({});
    setSelectedInspo([]);
    setClientPhotos([]);
    setResult(null);
    setExpandedSections({ info: true, service: true, prefs: true, inspo: false, photos: false });
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/events/${eventId}/public-services`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
      }
    } catch (err) { console.error("Failed to fetch services:", err); }
  };

  const fetchHostLooks = async () => {
    try {
      const res = await fetch(`${API}/events/${eventId}/host-looks`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setFetchedLooks(data.public_looks || data.looks || []);
      }
    } catch (err) { console.error("Failed to fetch host looks:", err); }
  };

  const selectExistingClient = (client) => {
    setSelectedClient(client);
    setFirstName(client.first_name || (client.name || "").split(" ")[0] || "");
    setLastName(client.last_name || (client.name || "").split(" ").slice(1).join(" ") || "");
    setEmail(client.email || "");
    setPhone(client.phone || "");
    // Load existing preferences
    if (client.preferences) {
      setPrefs(client.preferences);
    }
    if (client.service_id) setServiceId(client.service_id);
    if (client.inspo?.length) setSelectedInspo(client.inspo);
    setShowClientSearch(false);
  };

  const toggleInspoLook = (look) => {
    setSelectedInspo(prev => {
      const exists = prev.some(i => i.look_id === look.look_id);
      if (exists) return prev.filter(i => i.look_id !== look.look_id);
      return [...prev, { look_id: look.look_id, image_url: look.image_url, title: look.title, source: "host_portfolio" }];
    });
  };

  const handleUploadClientPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("rsvp_id", selectedClient?.rsvp_id || "");
      const res = await fetch(`${API}/host/events/${eventId}/client-photos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setClientPhotos(prev => [...prev, data.image_url]);
        toast.success("Photo uploaded");
      } else {
        toast.error("Failed to upload photo");
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const setPref = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  };

  const toggleMultiPref = (key, value) => {
    setPrefs(prev => {
      const current = prev[key] || [];
      return { ...prev, [key]: current.includes(value) ? current.filter(v => v !== value) : [...current, value] };
    });
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) { toast.error("First name is required"); return; }
    setLoading(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const body = {
        name: fullName, first_name: firstName.trim(), last_name: lastName.trim(),
        email, phone, service_id: serviceId || undefined,
        appointment_date: appointmentDate || undefined,
        notes,
        prefill_preferences: prefs,
        inspo_ids: selectedInspo.map(i => i.look_id),
        client_photo_urls: clientPhotos,
        existing_rsvp_id: selectedClient?.rsvp_id || undefined
      };
      const res = await fetch(`${API}/host/events/${eventId}/invite-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        toast.success("Client invited!");
        if (onSuccess) onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to invite client");
      }
    } catch (err) {
      toast.error("Failed to invite client");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const filteredClients = existingClients.filter(c =>
    !searchQuery || (c.name || "").toLowerCase().includes(searchQuery.toLowerCase()) || (c.email || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CatIcon = getCategoryIcon(pilotCategory);
  const accentColor = brandColor || "#7c3aed";
  const allLooks = fetchedLooks.length > 0 ? fetchedLooks : hostLooks;

  // Iter 9.47.h - assemble the list of preference fields to render.
  // Universal defaults (allergies/accessibility/dietary) now appear AT THE
  // BOTTOM of the form, just above the Notes section + submit, so the host
  // sees experience-specific options first. Notes always render last.
  const dynamicReady = Array.isArray(dynamicFields);
  const allCategoryFields = dynamicReady ? dynamicFields : [];
  const selectedIds = hostPrefConfig?.selected_field_ids;
  // Filter out universal fields (allergies/dietary/accessibility) from the
  // category-specific list - they're rendered separately below.
  const UNIVERSAL_KEYS = new Set(["allergies", "dietary", "accessibility"]);
  const enabledCategoryFields = (selectedIds
    ? allCategoryFields.filter(f => selectedIds.includes(f.field_id) || selectedIds.includes(f.key))
    : allCategoryFields
  ).filter(f => !UNIVERSAL_KEYS.has(f.key));

  // Universal toggles the host has required for this experience (rendered last).
  const universalEnabled = [];
  if (hostPrefConfig?.require_allergies !== false) {
    const allergiesField = allCategoryFields.find(f => f.key === "allergies");
    if (allergiesField) universalEnabled.push(allergiesField);
    else if (universalDefaults.allergies) universalEnabled.push({ ...universalDefaults.allergies, field_id: "allergies" });
  }
  if (hostPrefConfig?.require_accessibility) {
    const accField = allCategoryFields.find(f => f.key === "accessibility");
    if (accField) universalEnabled.push(accField);
    else if (universalDefaults.accessibility) universalEnabled.push({ ...universalDefaults.accessibility, field_id: "accessibility" });
  }
  if (hostPrefConfig?.require_dietary) {
    const dietField = allCategoryFields.find(f => f.key === "dietary");
    if (dietField) universalEnabled.push(dietField);
    else if (universalDefaults.dietary) universalEnabled.push({ ...universalDefaults.dietary, field_id: "dietary" });
  }

  // Final list: category fields first, then universal toggles at the bottom.
  // Notes (textarea) is the very last item before the submit button.
  const renderFields = [...enabledCategoryFields, ...universalEnabled];

  // Success view
  if (result) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); } }}>
        <DialogContent className="max-w-md" data-testid="invite-client-success-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              Client Invited
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-xl border-2 border-green-200 bg-green-50">
              <p className="text-sm font-medium text-green-800 mb-2">Invite link ready to share</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={result.invite_url} className="text-xs font-mono bg-white" data-testid="invite-link-output" />
                <Button variant="outline" size="sm" className="shrink-0" data-testid="copy-invite-link-btn"
                  onClick={() => { navigator.clipboard.writeText(result.invite_url); toast.success("Link copied!"); }}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-green-600 mt-2">Share this link with {firstName} to complete their RSVP with your prefilled preferences.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={resetForm} data-testid="invite-another-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Invite Another
              </Button>
              <Button className="flex-1" onClick={onClose} data-testid="invite-done-btn" style={{ backgroundColor: accentColor, color: "#fff" }}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Render preference field
  const renderField = (fieldKey, config) => {
    if (config.type === "pill-select") {
      return (
        <div key={fieldKey} className="mb-3">
          <Label className="text-xs text-gray-600 mb-1.5 block">{config.label}</Label>
          <div className="flex flex-wrap gap-1.5">
            {config.options.map(opt => (
              <button key={opt} onClick={() => setPref(fieldKey, opt)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  prefs[fieldKey] === opt ? "text-white shadow-sm" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                style={prefs[fieldKey] === opt ? { backgroundColor: accentColor } : {}}
                data-testid={`pref-${fieldKey}-${opt.toLowerCase().replace(/\s/g, '-')}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (config.type === "multi-pill") {
      return (
        <div key={fieldKey} className="mb-3">
          <Label className="text-xs text-gray-600 mb-1.5 block">{config.label}</Label>
          <div className="flex flex-wrap gap-1.5">
            {config.options.map(opt => {
              const selected = (prefs[fieldKey] || []).includes(opt);
              return (
                <button key={opt} onClick={() => toggleMultiPref(fieldKey, opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                    selected ? "text-white shadow-sm" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  style={selected ? { backgroundColor: accentColor } : {}}
                  data-testid={`pref-${fieldKey}-${opt.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {opt} {selected && <Check className="w-3 h-3" />}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    if (config.type === "text") {
      return (
        <div key={fieldKey} className="mb-3">
          <Label className="text-xs text-gray-600 mb-1 block">{config.label}</Label>
          <Input value={prefs[fieldKey] || ""} onChange={(e) => setPref(fieldKey, e.target.value)}
            placeholder={config.placeholder} className="text-sm" data-testid={`pref-${fieldKey}`} />
        </div>
      );
    }
    if (config.type === "textarea") {
      return (
        <div key={fieldKey} className="mb-3">
          <Label className="text-xs text-gray-600 mb-1 block">{config.label}</Label>
          <Textarea value={prefs[fieldKey] || ""} onChange={(e) => setPref(fieldKey, e.target.value)}
            placeholder={config.placeholder} rows={2} className="text-sm resize-none" data-testid={`pref-${fieldKey}`} />
        </div>
      );
    }
    if (config.type === "number") {
      return (
        <div key={fieldKey} className="mb-3">
          <Label className="text-xs text-gray-600 mb-1 block">{config.label}</Label>
          <Input type="number" value={prefs[fieldKey] || ""} onChange={(e) => setPref(fieldKey, e.target.value)}
            placeholder={config.placeholder} className="text-sm w-32" data-testid={`pref-${fieldKey}`} />
        </div>
      );
    }
    return null;
  };

  // Collapsible section wrapper
  const Section = ({ id, icon: Icon, title, badge, children }) => {
    const isExpanded = expandedSections[id];
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button onClick={() => toggleSection(id)}
          className="w-full flex items-center gap-2.5 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          data-testid={`section-toggle-${id}`}
        >
          <Icon className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-800 flex-1">{title}</span>
          {badge && <Badge variant="secondary" className="text-[10px]">{badge}</Badge>}
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {isExpanded && <div className="px-4 py-3 space-y-1">{children}</div>}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="invite-client-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" style={{ color: accentColor }} />
            Invite Client
          </DialogTitle>
          <DialogDescription>
            Prefill their preference card and send a personalized invite link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Client Search */}
          {existingClients.length > 0 && showClientSearch && (
            <div className="relative" data-testid="client-search-section">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search existing clients..."
                  className="pl-9 text-sm"
                  data-testid="client-search-input"
                />
              </div>
              {searchQuery && filteredClients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClients.slice(0, 8).map(c => (
                    <button key={c.rsvp_id || c.email || c.name} onClick={() => selectExistingClient(c)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left border-b border-gray-100 last:border-0"
                      data-testid={`client-result-${c.rsvp_id || 'none'}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                        <p className="text-xs text-gray-500 truncate">{c.email || "No email"}</p>
                      </div>
                      {c.status && <Badge variant="outline" className="text-[10px] shrink-0">{c.status}</Badge>}
                    </button>
                  ))}
                </div>
              )}
              {selectedClient && (
                <div className="mt-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">{selectedClient.name}</span>
                  </div>
                  <button onClick={() => { setSelectedClient(null); setShowClientSearch(true); setSearchQuery(""); }} className="text-xs text-purple-600 hover:underline">
                    Clear
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or enter new client</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </div>
          )}

          {/* Section 1: Client Info */}
          <Section id="info" icon={User} title="Client Info">
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">First Name *</Label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className="text-sm" data-testid="invite-first-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Last Name</Label>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" className="text-sm" data-testid="invite-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-600">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="text-sm" data-testid="invite-email" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Phone</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="text-sm" data-testid="invite-phone" />
                </div>
              </div>
            </div>
          </Section>

          {/* Section 2: Service & Appointment */}
          <Section id="service" icon={Sparkles} title="Service & Appointment" badge={serviceId ? "Selected" : undefined}>
            <div className="space-y-2.5">
              {services.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-600">Service</Label>
                  <Select value={serviceId} onValueChange={setServiceId}>
                    <SelectTrigger data-testid="invite-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(svc => (
                        <SelectItem key={svc.service_id} value={svc.service_id}>
                          {svc.name}{svc.price ? ` - $${svc.price}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-gray-600">Appointment Date</Label>
                <Input type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)}
                  className="text-sm" data-testid="invite-date" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Internal Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  placeholder="Notes for your team (not visible to client)" className="text-sm resize-none" data-testid="invite-notes" />
              </div>
            </div>
          </Section>

          {/* Section 3: Category Preferences (Iter 9.47.g - fully dynamic from
              Airtable Preference Fields. Host can toggle which fields appear
              via /host/{id}/experiences/{eid}/preference-config). */}
          {renderFields.length > 0 && (
            <Section id="prefs" icon={CatIcon}
              title={`${(pilotCategory || "").charAt(0).toUpperCase() + (pilotCategory || "").slice(1)} Preferences`}
              badge={Object.keys(prefs).filter(k => prefs[k] && (Array.isArray(prefs[k]) ? prefs[k].length > 0 : true)).length > 0
                ? `${Object.keys(prefs).filter(k => prefs[k] && (Array.isArray(prefs[k]) ? prefs[k].length > 0 : true)).length} filled`
                : undefined}
            >
              {renderFields.map((f) => {
                // Adapt the dynamic field to the legacy renderField config shape.
                const adapted = {
                  label: f.label,
                  type: f.type === "pill-select" ? "pill-select"
                       : f.type === "multi-pill" ? "multi-pill"
                       : f.type === "textarea" ? "textarea"
                       : f.type === "number" ? "number"
                       : "text",
                  options: f.options || [],
                  placeholder: f.placeholder || f.helper_text,
                };
                return renderField(f.key || f.field_id, adapted);
              })}
            </Section>
          )}

          {/* Section 4: Inspiration from Host Portfolio */}
          {allLooks.length > 0 && (
            <Section id="inspo" icon={Heart} title="Inspiration from Your Portfolio"
              badge={selectedInspo.length > 0 ? `${selectedInspo.length} selected` : undefined}>
              <div className="grid grid-cols-4 gap-1.5">
                {allLooks.map(look => {
                  const isSelected = selectedInspo.some(i => i.look_id === look.look_id);
                  return (
                    <button key={look.look_id} onClick={() => toggleInspoLook(look)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected ? "border-purple-500 ring-2 ring-purple-200" : "border-transparent hover:border-gray-300"
                      }`}
                      data-testid={`invite-inspo-${look.look_id}`}
                    >
                      {look.image_url ? (
                        <img src={look.image_url} alt={look.title || ""} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                          <Check className="w-5 h-5 text-white drop-shadow" />
                        </div>
                      )}
                      <div className="absolute bottom-0 inset-x-0 bg-black/50 px-1 py-0.5">
                        <p className="text-[8px] text-white truncate">{look.title}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Section 5: Private Client Photos */}
          <Section id="photos" icon={Camera} title="Client Photos (Private)"
            badge={clientPhotos.length > 0 ? `${clientPhotos.length} photos` : undefined}>
            <p className="text-xs text-gray-500 mb-2">
              Upload private photos of your client (before/after, reference shots). Only visible to you on their preference card.
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {clientPhotos.map((url, idx) => (
                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border group">
                  <img src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setClientPhotos(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`remove-client-photo-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {clientPhotos.length < 10 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 transition-colors"
                  data-testid="upload-client-photo-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mb-0.5" />
                      <span className="text-[10px]">Upload</span>
                    </>
                  )}
                  <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadClientPhoto(f); e.target.value = ''; }} />
                </label>
              )}
            </div>
          </Section>
        </div>

        {/* Footer */}
        <DialogFooter className="mt-4 gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !firstName.trim()}
            style={{ backgroundColor: accentColor, color: "#fff" }} data-testid="send-invite-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
